import React, { useState } from "react";
import { Col, Button, Row, Badge, Menu } from "antd";
import {
  CustomButton,
  BurgerIcon,
  NotificationIcon,
  useWindowsSize,
  LandIcon,
  PaymentsIcon,
  SettingsIcon,
  BidsIcon,
  FileIcon,
} from "@cashrent/commonground-elements";
import styled from "styled-components";
import { GetSiteConfigs } from "../../../../hooks/useChangeGlobalStyles";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "./TopMenu.scss";
import AuthMenu from "../auth-menu/AuthMenu";
import useNotification from "../../../../hooks/useNotification";
import NotificationsPanel from "../notification/NotificationsPanel";
import { isPartnerSite, redirectToNewApp } from "../../../../utils";

export const PrivateAccess = [
  {
    path: "/app/my-account/farms",
    icon: LandIcon,
    label: "Land Manager",
  },
  {
    path: "/app/my-account/bidding/my-bid",
    icon: BidsIcon,
    label: "Bids",
  },
  {
    path: "/app/my-account/payments",
    icon: PaymentsIcon,
    label: "Payments",
  },
  {
    path: "/app/my-account/bidding/my-agreements-list",
    icon: FileIcon,
    label: "Agreements",
  },
  {
    path: "/app/my-account/settings/notifications",
    icon: NotificationIcon,
    label: "Notifications",
  },
  {
    path: "/app/my-account/settings/account-information",
    icon: SettingsIcon,
    label: "Settings",
  },
];

export const UserAccess = [{
  label: "Farmer profile",
  path: "/app/my-account/settings/farmer-profile",
}, {
  label: "Account settings",
  path: "/app/my-account/settings/account-information",
}];

export const PublicAccess = [{
  path: "/app/lands/search/40.41500741724772/-99.8720887852595/4/sale/all/all",
  label: "Buy",
}, {
  path: "/app/sell",
  label: "Sell",
}, {
  path: "/app/lands/search/40.41500741724772/-99.8720887852595/4/lease/all/all",
  label: "Lease",
}, {
  path: "/app/sell#lease-your-land",
  label: "List",
}, {
  path: "/app/cashrentstimate",
  label: "CashRentstimate",
}, {
  path: `${process.env.REACT_APP_DOMAIN}/app/insurance`,
  label: "Insurance",
  children: [{
    path: `${process.env.REACT_APP_DOMAIN}/app/insurance`,
    label: "Overview",
  }, {
    path: `${process.env.REACT_APP_DOMAIN}/app/insurance/our-solutions`,
    label: "Our solutions",
  }, {
    path: `${process.env.REACT_APP_DOMAIN}/app/insurance/flex-lease-insurance`,
    label: "Flex lease",
  }, {
    path: `${process.env.REACT_APP_DOMAIN}/app/insurance/cash-lease-insurance-protection`,
    label: "Cash lease",
  }, {
    path: `${process.env.REACT_APP_DOMAIN}/app/insurance/agents`,
    label: "Meet the agents",
  }, {
    path: `${process.env.REACT_APP_DOMAIN}/app/insurance/contact`,
    label: "Contact",
  }],
}, {
  path: "/app/contact-us",
  label: "Contact us",
}];

export const WhiteLabeledAccess = [
  {
    path: "/app",
    label: "Home",
  },
  {
    path: "/app/lands/search/40.41500741724772/-99.8720887852595/4?transactionType=SALE",
    label: "Buy",
  },
  {
    path: "/app/lands/search/40.41500741724772/-99.8720887852595/4?transactionType=LEASE",
    label: "Lease",
  },
];

export const WhiteLabeledPrivateAccess = [
  {
    path: "/my-account/bidding/my-bid",
    icon: BidsIcon,
    label: "Bids",
  },
  {
    path: "/my-account/payments",
    icon: PaymentsIcon,
    label: "Payments",
  },
  {
    path: "/my-account/bidding/my-agreements-list",
    icon: FileIcon,
    label: "Agreements",
  },
  {
    path: "/my-account/settings/notifications",
    icon: NotificationIcon,
    label: "Notifications",
  },
  {
    path: "/my-account/settings/account-information",
    icon: SettingsIcon,
    label: "Settings",
  },
];

const TopMenu = ({
  variant = "white",
}) => {
  const SiteConfigs = GetSiteConfigs() || null;
  const currentUser = useSelector((state) => (state.user.user));
  const isMobile = useWindowsSize();
  const history = useHistory();
  const { onFetchNotifications, notifications, hasNotifications } = useNotification();
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  React.useEffect(() => {
    if (currentUser?.email) {
      onFetchNotifications();
      if (currentUser.isAdmin && UserAccess.every((item) => item.label !== "Management")) {
        UserAccess.push({
          label: "Management",
          path: "/admin/listings",
        });
      }
    }
  }, [currentUser]);

  const handleOpenDrawer = () => {
    setIsDrawerVisible(true);
  };

  return (
    <React.Fragment>
      <Container data-variant={variant} align="middle" justify="space-between">
        <Col md={isMobile ? 12 : 4}>
          <LogoContainer onClick={() => redirectToNewApp("/app")} >
            <BrandImage
              alt="Logo"
              src={SiteConfigs?.logo}
              height={45}
              width={175}
            />
          </LogoContainer>
        </Col>
        {!isMobile && (
          <Col md={16} className="top-menu">
            <NavigationMenu
              onClick={(event) => {
                if (event.key.startsWith("http")) {
                  window.location.assign(event.key);
                } else {
                  redirectToNewApp(event.key);
                }
              }}
              mode="horizontal"
              items={isPartnerSite() ? WhiteLabeledAccess.map((item) => ({
                label: item.label,
                key: item.path,
              })) : PublicAccess.map((item) => ({
                label: item.label,
                key: item.path,
                children: item.children?.map((child) => ({
                  label: child.label,
                  key: child.path,
                })),
              }))}
              selectedKeys={[history.location.pathname]}
              data-variant={variant}
            />
          </Col>
        )}
        <Col md={isMobile ? 12 : 4}>
          <AuthButtons>
              {!isMobile && currentUser.email && notifications && (
                <NotificationsPanel data={notifications}>
                  <CustomButton
                    type="text"
                    shape="circle"
                    icon={
                      <DotBadge dot={hasNotifications}>
                        <NotificationIcon />
                      </DotBadge>
                    }
                  />
                </NotificationsPanel>
              )}
              <AuthMenu
                hasNotifications={notifications && notifications.data.length > 0}
                hasDrawerOpen={isDrawerVisible}
                currentUser={currentUser}
                onDrawerClose={() => setIsDrawerVisible(false)}
                privateNavItems={isPartnerSite() ? WhiteLabeledPrivateAccess : PrivateAccess}
                publicNavItems={isPartnerSite() ? WhiteLabeledAccess : PublicAccess}
                userNavItems={UserAccess}
              />
              </AuthButtons>
          {!currentUser.email && (
            <AuthButtons>
              {!isMobile && (
                <React.Fragment>
                  <LoginUpButton
                    // data-auth

                    type="link"
                    onClick={() => history.push("/login")}
                  >
                    Log in
                  </LoginUpButton>
                  <CustomButton data-auth ghost onClick={() => history.push("/register")}>
                    Sign up
                  </CustomButton>
                </React.Fragment>
              )}
              {isMobile && (
                <Button type="text" shape="circle" icon={<BurgerIcon />} onClick={() => handleOpenDrawer()}/>
              )}
            </AuthButtons>
          )}
        </Col>
      </Container>
    </React.Fragment>
  );
};

const Container = styled(Row)`
  display: flex;
  padding: 0;
  min-height: 77px;
  min-width: 100%;
  padding-left: 30px;
  padding-right: 30px;
  box-shadow: 0px 0px 10px rgba(84, 84, 84, 0.05);

  &[data-variant="white"] {
    background-color: white;
  }

  &[data-variant="transparent"] {
    background-color: transparent;
  }

  &[data-variant="custom"] {
    background-color: var(--navbar-background-color);
  }
`;
const AuthButtons = styled.div`
  display: flex;
  justify-content: end;
  gap: 20px;

  [data-auth="true"] {
    color: black;
    padding: 5px 30px;

    &:active {
      color: black;
    }

    &:focus {
      color: ${(props) => props.theme.color.grey.light[2]} !important;
    }

    &:hover {
      color: ${(props) => props.theme.color.grey.light[1]} !important;
    }

    &.ant-btn-background-ghost {
      border-color: black;
      &:hover {
        border-color: ${(props) => props.theme.color.grey.light[1]} !important;
      }

      &:focus {
        border-color: ${(props) => props.theme.color.grey.light[2]} !important;
      }
    }
  }
`;

 const NavigationMenu = styled(Menu)`
  align-items: center;
  justify-content: center;
  margin: 0;
  border-bottom: none;
  text-align: center;
  border-right: 0;
  .ant-menu-title-content {
    font-size: ${(p) => p.theme.font.size[15]};
    font-weight: ${(p) => p.theme.font.weight.bold};
  }

  &:not(.ant-menu-dark) > .ant-menu-item:hover,
  &:not(.ant-menu-dark) > .ant-menu-submenu:hover,
  &:not(.ant-menu-dark) > .ant-menu-item-active,
  &:not(.ant-menu-dark) > .ant-menu-submenu-active,
  &:not(.ant-menu-dark) > .ant-menu-item-open,
  &:not(.ant-menu-dark) > .ant-menu-submenu-open,
  &:not(.ant-menu-dark) > .ant-menu-item-selected,
  &:not(.ant-menu-dark) > .ant-menu-submenu-selected {
    color: var(--button-background-color);
  }

  &:not(.ant-menu-dark) {
    color: ${isPartnerSite() ? "white" : " black"};
    &[data-variant="custom"] {
      background-color: var(--navbar-background-color) !important;
    }
  }

  &:not(.ant-menu-dark) > .ant-menu-item:hover::after,
  &:not(.ant-menu-dark) > .ant-menu-item-active::after,
  &:not(.ant-menu-dark) > .ant-menu-item-open::after,
  &:not(.ant-menu-dark) > .ant-menu-item-selected::after {
    border-color: var(--button-background-color);
  }

  &:not(.ant-menu-dark) > .ant-menu-submenu::after,
  &:not(.ant-menu-dark) > .ant-menu-submenu-active::after,
  &:not(.ant-menu-dark) > .ant-menu-submenu-open::after,
  &:not(.ant-menu-dark) > .ant-menu-submenu-selected::after {
    content: none;
  }

  .ant-menu-submenu {
    border-bottom: none !important;
  }

  .ant-menu-submenu-title:hover {
    color: var(--button-background-color);
  }

  .ant-menu-item:active,
  .ant-menu-submenu-title:active {
    background-color: var(--primary-color-lighten-30);
  }

  &[data-variant="white"] {
    background-color: white;
  }

  &[data-variant="transparent"] {
    background-color: transparent;
    color: white;
  }

  &[data-variant="custom"] {
    background-color: var(--navbar-background-color) !important;
  }
`;

const LogoContainer = styled.a`
  max-width: 200px !important;
`;

const BrandImage = styled.img`
  object-fit: contain;
`;

const CustomBadge = styled(Badge)`
  .ant-badge-dot {
    top: 0;
    top: 4px;
    right: 2px;
    width: 11px;
    height: 11px;
    background-color: var(--primary-color);
  }
`;

export const DotBadge = (props) => {
  const { children, ...rest } = props;

  return (
    <CustomBadge {...rest}>
      {children}
    </CustomBadge>
  );
};

const LoginUpButton = styled(CustomButton)`
  color: ${isPartnerSite() ? "white !important" : "black !important"};
  :hover {
    color: var(--button-background-color) !important;
  }
`;

export default TopMenu;
